import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, Modal, Pressable, StyleSheet, View } from 'react-native';
import { TextInput } from '@app/components';
import { StyledForms } from '@app/components/styled/form/Form';
import { StyledInputs } from '@app/components/styled/form/Inputs';
import { Toggle } from '@app/components/form/Toggle';
import { FieldWithLabel } from '@app/components/styled/form/Label';
import { useFieldArray, UseFieldArrayRemove, useFormContext, useWatch } from 'react-hook-form';
import { Dropdown } from '@app/components/form/Dropdown';
import { DateTimePicker } from '@app/components/form/DateTimePicker.web';
import { FormSection } from '@app/components/styled/form/FormSection';
import { normalize } from '@app/helpers';
import styled from '@2pulse/responsive';
import { worksansoxford } from '@app/style';
import { useError } from '@app/contexts';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';
import { buildCountryList, getPhoneMaxDigits, extractCountryCodeFromPhone, Country } from '@tickeat/intl-phone-number';

const COUNTRIES: Country[] = buildCountryList();

const detectCountry = (phone: string): Country =>
  COUNTRIES.find(c => c.code === extractCountryCodeFromPhone(phone)) ?? COUNTRIES[0];

const PhoneInput = () => {
  const { setValue, formState } = useFormContext();
  const phoneValue = useWatch({ name: 'phone' }) ?? '';
  const error = useError('phone');
  const [selectedCountry, setSelectedCountry] = useState<Country>(() => detectCountry(phoneValue));
  const [showPicker, setShowPicker] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState({ x: 0, y: 0 });
  const [isBlurred, setIsBlurred] = useState(true);
  const containerRef = useRef<View>(null);

  /** Initialize the phone number with the international format if it's a valid french number */
  useEffect(() => {
    if (!phoneValue || phoneValue.startsWith('+')) {
      return;
    }

    const intlPhone = parsePhoneNumberFromString(phoneValue, selectedCountry.code)?.format('E.164');
    if (intlPhone) {
      setValue('phone', intlPhone);
    }
  }, [phoneValue]);

  const maxDigits = useMemo(() => getPhoneMaxDigits(selectedCountry.code), [selectedCountry.code]);

  const formattedPhone = useMemo(() => {
    if (!phoneValue) return '';
    const parsed = parsePhoneNumberFromString(phoneValue);
    const digits = parsed ? parsed.formatNational().replace(/\D/g, '') : phoneValue;
    return new AsYouType(selectedCountry.code).input(digits);
  }, [phoneValue, selectedCountry.code]);

  const handleChange = (text: string) => {
    const digits = text.replace(/\D/g, '');
    if (digits.length <= maxDigits) {
      const intlPhone = parsePhoneNumberFromString(digits, selectedCountry.code)?.format('E.164');
      setValue('phone', intlPhone ?? digits);
    }
  };

  const openPicker = () => {
    containerRef.current?.measure((_fx, _fy, _width, height, px, py) => {
      setPickerAnchor({ x: px, y: py + height + 4 });
      setShowPicker(true);
    });
  };

  return (
    <StyledInputs.Holder>
      <StyledInputs.Label isFilled={true} isDirty={formState.isDirty} isBlurred={isBlurred}>
        Téléphone
      </StyledInputs.Label>

      {!!error && (
        <StyledForms.Error>
          <StyledForms.ErrorLabel>{error}</StyledForms.ErrorLabel>
        </StyledForms.Error>
      )}

      <PhoneContainer ref={containerRef} isBlurred={isBlurred}>
        <CountrySelector onPress={openPicker}>
          <CountrySelectorText>{selectedCountry.flag} {selectedCountry.callingCode}</CountrySelectorText>
        </CountrySelector>

        <PhoneSeparator />

        <PhoneRawInput
          value={formattedPhone}
          onChangeText={handleChange}
          onFocus={() => setIsBlurred(false)}
          onBlur={() => setIsBlurred(true)}
          keyboardType="phone-pad"
        />
      </PhoneContainer>

      <Modal visible={showPicker} transparent animationType="fade" onRequestClose={() => setShowPicker(false)}>
        <Pressable style={StyleSheet.absoluteFillObject} onPress={() => setShowPicker(false)}>
          <PickerCard style={{ position: 'absolute', top: pickerAnchor.y, left: pickerAnchor.x }}>
            <FlatList
              data={COUNTRIES}
              keyExtractor={item => item.code}
              style={{ maxHeight: normalize(320) }}
              renderItem={({ item }) => (
                <PickerItem onPress={() => {
                  setSelectedCountry(item);
                  setValue('phone', '');
                  setShowPicker(false);
                }}>
                  <PickerItemFlag>{item.flag}  {item.callingCode}</PickerItemFlag>
                  <PickerItemName>{item.name}</PickerItemName>
                </PickerItem>
              )}
            />
          </PickerCard>
        </Pressable>
      </Modal>
    </StyledInputs.Holder>
  );
};

const AddressForm = ({ index, remove }: { index: number, remove: UseFieldArrayRemove }) => {
  const [isOpen, setIsOpen] = useState(false);

  const address = useWatch({
    name: `addresses.${index}`
  });

  const name = address?.name
    ? `Adresse: ${address.name}`
    : `Adresse: ${index + 1}`;

  return (
    <Dropdown
      label={name}
      isOpen={isOpen}
      handleOpen={setIsOpen}
      onDelete={() => remove(index)}>

      <TextInput
        name={`addresses.${index}.name`}
        label="Nom"
        autoCapitalize="none" />

      <TextInput
        name={`addresses.${index}.address`}
        label="Adresse"
        autoCapitalize="none" />

      <TextInput
        name={`addresses.${index}.door`}
        label="Numéro de porte"
        autoCapitalize="none" />

      <TextInput
        name={`addresses.${index}.floor`}
        label="Étage"
        autoCapitalize="none" />

      <TextInput
        name={`addresses.${index}.building`}
        label="Bâtiment"
        autoCapitalize="none" />

      <TextInput
        name={`addresses.${index}.buildingCode`}
        label="Code bâtiment"
        autoCapitalize="none" />

      <TextInput
        name={`addresses.${index}.intercom`}
        label="Intercom"
        autoCapitalize="none" />

      <TextInput
        name={`addresses.${index}.informations`}
        label="Informations"
        autoCapitalize="none" />

    </Dropdown>
  )
}

export const CustomerForm = () => {
  const { append, remove, fields } = useFieldArray({
    name: 'addresses',
  });

  return (
    <StyledForms.Form>
      <FormSection.Section>
        <FormSection.Title>Informations générales</FormSection.Title>
        <FormSection.Content>

          <TextInput
            name="firstname"
            label="Prénom"
            autoCapitalize="words"
          />

          <TextInput
            name="lastname"
            label="Nom"
            autoCapitalize="words"
          />

          <TextInput
            name="email"
            label="Email"
            autoCapitalize="none"
            keyboardType="email-address"
          />

          <PhoneInput />

          <DateTimePicker name="birthday" label="Date de naissance" />

          {fields.map((field, index) => (
            <AddressForm key={field.id} index={index} remove={remove} />
          ))}

        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Opt-in</FormSection.Title>
        <FormSection.Content>
          <FieldWithLabel label="Opt-in email">
            <Toggle
              name="optInEmail"
              options={[{ title: 'Oui', value: true }, { title: 'Non', value: false }]}
            />
          </FieldWithLabel>

          <FieldWithLabel label="Opt-in SMS">
            <Toggle
              name="optInSMS"
              options={[{ title: 'Oui', value: true }, { title: 'Non', value: false }]}
            />
          </FieldWithLabel>

          <FieldWithLabel label="Opt-in fidélité">
            <Toggle
              name="optInLoyalty"
              options={[{ title: 'Oui', value: true }, { title: 'Non', value: false }]}
            />
          </FieldWithLabel>
        </FormSection.Content>
      </FormSection.Section>

    </StyledForms.Form>
  );
};

const PhoneContainer = styled.View<{ isBlurred: boolean }>(({ theme, props }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  height: normalize(76),
  borderRadius: 5,
  backgroundColor: 'rgba(240, 249, 252, .04)',
  borderBottomColor: props.isBlurred ? 'rgba(255,255,255,0)' : theme.colors.celadonblue,
  borderBottomWidth: 3,
  overflow: 'hidden',
}));

const CountrySelector = styled.TouchableOpacity({
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  paddingHorizontal: normalize(21),
});

const CountrySelectorText = styled.Text(({ theme }) => ({
  ...worksansoxford,
  fontSize: normalize(21),
  fontWeight: '500',
  color: theme.colors.babypowder,
}));

const PhoneSeparator = styled.View(({ theme }) => ({
  width: 1,
  height: normalize(34),
  backgroundColor: 'rgba(255,255,255,0.15)',
}));

const PhoneRawInput = styled.TextInput(({ theme }) => ({
  flex: 1,
  height: '100%',
  ...worksansoxford,
  fontSize: normalize(21),
  fontWeight: '500',
  color: theme.colors.babypowder,
  paddingHorizontal: normalize(21),
  outlineWidth: 0,
  backgroundColor: 'transparent',
}));

const PickerCard = styled.View({
  backgroundColor: '#02182B',
  borderRadius: 12,
  borderWidth: 1,
  borderColor: 'rgba(255,255,255,0.1)',
  width: normalize(320),
  overflow: 'hidden',
  shadowColor: '#000',
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.4,
  shadowRadius: 12,
  elevation: 8,
});

const PickerItem = styled.TouchableOpacity({
  flexDirection: 'row',
  alignItems: 'center',
  gap: normalize(12),
  paddingVertical: normalize(12),
  paddingHorizontal: normalize(16),
  borderBottomWidth: 1,
  borderBottomColor: 'rgba(255,255,255,0.06)',
  backgroundColor: '#02182B',
});

const PickerItemFlag = styled.Text(({ theme }) => ({
  ...worksansoxford,
  fontSize: normalize(18),
  color: theme.colors.babypowder,
}));

const PickerItemName = styled.Text(({ theme }) => ({
  ...worksansoxford,
  fontSize: normalize(18),
  color: theme.colors.babypowder,
  opacity: 0.7,
  flex: 1,
}));
